import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  DropdownButton,
  Icon,
  useTranslation,
  DebouncedSearchInput,
  InfiniteScrollV2,
  TruncatedText,
  DropdownItem,
  Empty,
} from '@testsigma/atoms';
import noResultFound from '@images/no-result-found.svg';
import { Project } from '../../models/project.model';
import { useGetAllProjects } from '../../services/project.service';
import { ListItemSkeleton } from '@modules/test-development/components/list-item-skeleton';
const { VirtualizedInfiniteLoader } = InfiniteScrollV2;
import classNames from 'classnames';
import { PROJECT_TYPE } from './constants';
import { UserPreferenceContext } from '@contexts/user-preference-context';
import { ApplicationType } from '@testsigmainc/constants';

type DropdownSelectionType = {
  selectedItem: Project | null;
  updateSelected: (data: Project) => void;
  dropdownClassName?: string;
  applicationType?: ApplicationType;
};

const ProjectAutoComplete: React.FC<DropdownSelectionType> = ({
  selectedItem,
  updateSelected,
  dropdownClassName = '',
  applicationType,
}) => {
  const userPreferenceContext = useContext(UserPreferenceContext);
  const projectPreference =
    userPreferenceContext?.userPreference.preference.projectPreference;
  const { t } = useTranslation();
  const [value, updateValue] = useState('');
  const [isOpen, updateIsOpen] = useState(false);
  const [projects, setProjects] = useState([]);

  const filterQuery = useMemo(() => {
    let query = 'lastAccessTime:true';
    if (value) {
      query += `,name:*${value}*`;
    }
    if (applicationType) {
      query += `,appType@${applicationType}`;
    }
    return query;
  }, [value, applicationType]);

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useGetAllProjects({
      filterQuery,
      keepPreviousData: !value,
    });

  useEffect(() => {
    let mergedContent = data?.pages?.flatMap((page) => page.content) || [];
    const { projectId, selectedProject } = projectPreference || {};

    if (!!value) {
      return setProjects(mergedContent);
    }

    let currentProject = mergedContent.find(
      (project: Project) => project.id === projectId,
    );

    if (projectId === selectedProject?.id && !currentProject) {
      mergedContent = [selectedProject, ...mergedContent];
      currentProject = selectedProject;
    }

    setProjects(mergedContent);

    if (projectId !== selectedProject?.id) {
      updateSelected(currentProject || mergedContent?.[0]);
    }
  }, [data, value]);

  const rowRenderer = useCallback(
    (index: number) => {
      const item = projects[index];
      const isProjectSelected = item?.id === selectedItem?.id;
      return (
        <DropdownItem
          key={item?.id}
          onClick={() => {
            updateSelected(item);
            updateIsOpen(false);
          }}
          className='flex items-center text-unique-600 group hover:!bg-secondary-50 hover:!text-unique-600'
          isSelected={isProjectSelected}
          role='row'
        >
          <Icon
            className='text-[18px] pr-md text-unique-800'
            name={
              item?.hasMultipleApps
                ? 'nav-bar-menu'
                : PROJECT_TYPE.icon[item?.projectType]
            }
          />
          <div className='flex w-full items-center justify-between' role='gridcell'>
            <div className='w-[150px]'>
              <TruncatedText value={item?.name} className='inline-block' />
            </div>
            <div className='flex items-center gap-x-2'>
              {isProjectSelected && (
                <Icon
                  name='check-circle'
                  className='text-primary-500 text-base'
                />
              )}
              {item?.isDemo && (
                <div
                  className={classNames(
                    'px-1 py-0.5 border text-xxs border-warning-500 bg-warning-50 rounded text-warning-600 hidden group-hover:flex',
                    !isProjectSelected ? 'ml-5' : '',
                  )}
                >
                  DEMO
                </div>
              )}
            </div>
          </div>
        </DropdownItem>
      );
    },
    [projects, selectedItem],
  );

  return (
    <DropdownButton
      name={
        <div className='flex items-center'>
          <Icon
            className='text-[18px] pr-md text-unique-800'
            name={
              selectedItem?.hasMultipleApps
                ? 'nav-bar-menu'
                : PROJECT_TYPE.icon[selectedItem?.projectType]
            }
          />
          {selectedItem?.name}
        </div>
      }
      label={t('project_switcher.project')}
      variant='select-fill'
      placement='bottom'
      dropdownClassName={classNames(
        '!min-h-[240px] !max-h-[240px] !overflow-auto',
        dropdownClassName,
      )}
      wrapperClassName='mb-[18px]'
      labelClassName='mb-1'
      onClick={() => {
        updateValue('');
      }}
      hideOnChildClick={false}
      setIsOpen={updateIsOpen}
      isOpen={isOpen}
    >
      <div>
        <DebouncedSearchInput
          value={value}
          onChange={(val: string) => updateValue(val)}
          placeholder={t('common.search')}
          wrapperClassName='outline-none w-full bg-white p-md sticky top-0 z-10'
        />
        {isFetching && !isFetchingNextPage ? (
          <ListItemSkeleton />
        ) : projects.length ? (
          <VirtualizedInfiniteLoader
            rowHeight={35}
            listHeight={175}
            rowRenderer={rowRenderer}
            rowCount={projects.length || 0}
            items={projects}
            loadMore={fetchNextPage}
            hasMore={hasNextPage}
            isLoading={isFetching}
            totalPages={data?.pages?.[0]?.totalPages ?? 0}
            LoadingComponent={<ListItemSkeleton />}
          />
        ) : (
          <Empty
            description={<span>{t('no.results.found')}</span>}
            image={
              <img
                src={noResultFound}
                alt='empty-state-logo'
                className='mb-3'
              />
            }
          />
        )}
      </div>
    </DropdownButton>
  );
};

export default ProjectAutoComplete;
